import React, { useEffect, useState } from "react";
import { FeatherAlertCircle } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { ReportsLayout } from "../layout/ReportsLayout";
import { ReportFilterBar, ReportFilters } from "../components/ReportFilterBar";
import { ReportTree } from "../components/ReportTree";
import { useZohoReport } from "../hooks/useZohoReport";
import { ZOHO_REPORTS, ZohoReportSlug } from "../config/reportsNavigation";
import { formatYmd, resolvePreset } from "../config/dateRanges";

interface ZohoReportPageProps {
  slug: ZohoReportSlug;
}

const defaultFilters = (): ReportFilters => {
  const r = resolvePreset("ThisMonth", { from: "", to: "" });
  return { preset: "ThisMonth", from: r.from, to: r.to, basis: "accrual" };
};

/**
 * One Zoho-backed statement: Zoho's filter bar + the hierarchical table.
 * Live on every view — no cache, no refresh button.
 */
export const ZohoReportPage: React.FC<ZohoReportPageProps> = ({ slug }) => {
  const def = ZOHO_REPORTS[slug];
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const { data, loading, error, range, retry } = useZohoReport(slug, filters, def.hasBasis);

  // Switching reports keeps the range but drops a basis Cash Flow cannot use.
  useEffect(() => {
    if (!def.hasBasis) setFilters((f) => (f.basis === "cash" ? { ...f, basis: "accrual" } : f));
  }, [def.hasBasis]);

  const onChange = (next: ReportFilters) => {
    // Leaving Custom for a preset: seed the custom pickers with that preset's
    // window so switching back to Custom starts from something sensible.
    if (next.preset !== "CustomDate") {
      const r = resolvePreset(next.preset, { from: next.from, to: next.to });
      next = { ...next, from: r.from, to: r.to };
    }
    setFilters(next);
  };

  const heading = def.asOf
    ? `As of ${formatYmd(range.to)}`
    : `${formatYmd(range.from)} – ${formatYmd(range.to)}`;
  const basisLabel = data?.page_context.report_basis ?? (def.hasBasis ? (filters.basis === "cash" ? "Cash" : "Accrual") : null);

  return (
    <ReportsLayout>
      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">{def.title}</span>
          <span className="text-body font-body text-subtext-color">
            {heading}
            {basisLabel ? ` · ${basisLabel} basis` : ""}
          </span>
        </div>

        <ReportFilterBar
          filters={filters}
          asOf={def.asOf}
          hasBasis={def.hasBasis}
          disabled={loading}
          onChange={onChange}
        />

        {error ? (
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex max-w-md flex-col items-center gap-4 text-center">
              <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
              <span className="text-heading-3 font-heading-3 text-default-font">
                We couldn't load this report
              </span>
              <span className="text-body font-body text-subtext-color">{error}</span>
              <Button onClick={retry}>Try again</Button>
            </div>
          </div>
        ) : loading && !data ? (
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
              <span className="text-body font-body text-subtext-color">Fetching from Zoho Books…</span>
            </div>
          </div>
        ) : data?.root ? (
          <div className={loading ? "opacity-60 transition-opacity" : ""}>
            <ReportTree root={data.root} columns={data.columns} debitCredit={def.debitCredit} />
          </div>
        ) : (
          <span className="text-body font-body text-subtext-color">Zoho returned no rows for this period.</span>
        )}
      </div>
    </ReportsLayout>
  );
};
